import { useEffect, useMemo, useState } from 'react';

import { useGetStatesQuery } from 'api/lookups/getStates';
import type { Area } from 'api/types/lookups';
import { useGetZipcodesMutation } from 'api/zipcodes/getZipcode';

interface IOption {
    value: string;
    label: string;
    state: string;
}

type LookupTable = { [k: string]: { state: string; city: string } };

interface IUseOptionsWithSearchProps {
    search: string;
    limitOptionCount?: number;
    /**
     * Fill in this value if you want to ensure it's option is included in final array
     */
    prefetchValue?: string;
}

const transformToLookupTable = (zipCodes: Area[]) => {
    return zipCodes.reduce<LookupTable>((acc, area) => {
        area.zipCodes.forEach((zipCode) => {
            acc[zipCode] = { state: area.state, city: area.city };
        });
        return acc;
    }, {});
};

const createOption = ({
    code,
    city,
    state,
}: {
    code: string;
    state: string;
    city: string;
}): IOption => ({
    value: code,
    label: `${city}, ${state} ${code}`,
    state,
});

const MAX_OPTION_COUNT = 100;

/**
 * Does the heavy lifting of getting options, transforming them and enabling search
 */
export const useOptionsWithSearch = ({
    search,
    limitOptionCount,
    prefetchValue,
}: IUseOptionsWithSearchProps) => {
    const [searchOption, setSearchOption] = useState<IOption>();
    const { data: lookupData, isLoading: isLoadingLookup } = useGetStatesQuery(
        {}
    );

    const {
        data: searchData,
        mutate: searchApi,
        error: searchError,
        isLoading: searchIsLoading,
        variables,
    } = useGetZipcodesMutation({});

    // Creates lookup table of all options
    const allOptions = useMemo(
        () =>
            Object.entries(
                transformToLookupTable(lookupData?.cityZipCodes ?? [])
            ).map(([key, value]) => createOption({ code: key, ...value })),
        [lookupData]
    );

    // Creates options based on search result
    useEffect(() => {
        if (searchData) {
            setSearchOption(
                createOption({ code: variables?.code || '', ...searchData })
            );
        }
    }, [variables, searchData]);

    useEffect(() => {
        if (prefetchValue) {
            searchApi({ code: prefetchValue });
        }
    }, [prefetchValue, searchApi]);

    // Adds search option, filter out others and limit to limitOptionCount
    const options = useMemo(() => {
        return [
            searchOption,
            ...allOptions.filter(({ value }) => value.includes(search)),
        ]
            .filter(Boolean)
            .splice(0, limitOptionCount ?? MAX_OPTION_COUNT) as IOption[];
    }, [searchOption, allOptions, search, limitOptionCount]);

    return {
        isLoading: isLoadingLookup || searchIsLoading,
        error: searchError,
        options,
        search: searchApi,
        foundSearchOption: searchOption,
        clear: () => setSearchOption(undefined),
    };
};
